import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { styled, Grid2, Snackbar, Alert } from '@mui/material'
import {
  Searchbar,
  FilterButton,
  AreaManagementProvinceTable,
  AreaManagementAreaTable,
  AreaManagementSupervisorTable,
  AreaManagementAreaControllerTable,
  AreaManagementSetupAreaSupervisorTable,
  AreaManagementMaintenanceAdminTable,
} from '@/components/index'
import { useAreaManagementContext } from '@/context/AreaManagementContext'
import { ManagementTabType, FilterState } from '@/utilities/types/filters'
import SyncIcon from '@/assets/icons/sync.svg'
import AssignIcon from '@/assets/icons/assign.svg'
import { mutate } from 'swr'
import {
  AreaData,
  SupervisorData,
  AreaControllerData,
  ProvinceData,
} from '@/utilities/types/table'
import { FilterConfig } from '@/utilities/types/shared-filter'
import {
  areaControllerFilterConfig,
  areaManagementFilterConfig,
  createProvinceManagementFilterConfig,
  supervisorFilterConfig,
} from '@/utilities/constants/filterConfigs'
import { syncArea, syncProvinces } from '@/api'
import { FilterAreaControllerModal } from '@/components/filter/filter-area-controller/FilterAreaControllerModal'
import { useAlert } from '@/utilities/useAlert'
import { FilterProvincesModal } from '@/components/filter/filter-provinces/FilterProvincesModal'
import { FilterAreasModal } from '@/components/filter/filter-areas/FilterAreasModal'
import { FilterSupervisorsModal } from '@/components/filter/filter-supervisors/FilterSupervisorsModal'
import { FilterSetupAreaSupervisorModal } from '@/components/filter/filter-setup-area-supervisor/FilterSetupAreaSupervisorModal'
import { FilterMaintenanceAdminModal } from '@/components/filter/filter-maintenance-admin/FilterMaintenanceAdminModal'
import { AreaManagementForm } from './area-management-form/AreaManagementForm'
import { useForm } from '@/utilities'
import FilterIcon from '@/assets/icons/filter.svg'
import { useNavigate } from 'react-router-dom'

interface ButtonTabProps {
  buttontype: ManagementTabType
  selected: ManagementTabType
}

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  color: theme.palette.text.primary,
  background: 'transparent',
  overflowX: 'hidden',
  overflowY: 'auto',
}))

const ButtonTab = styled('button')<ButtonTabProps>(
  ({ theme, buttontype, selected }) => ({
    minWidth: '120px',
    height: '33px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    border: `1px solid ${theme.palette.grey[400]}`,
    background:
      buttontype === selected ? theme.palette.secondary.main : 'transparent',
    color:
      buttontype === selected
        ? theme.palette.common.white
        : theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '12px',
    fontFamily: theme.typography.fontFamily,
    cursor: 'pointer',
    '&:focus': {
      outline: 'none',
    },
  }),
)

const ActionButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  width: '180px',
  height: '42px',
  borderRadius: '6px',
  background: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontFamily: theme.typography.fontFamily,
  fontSize: '12px',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
}))

type CurrentFilterState =
  | FilterState<ProvinceData>
  | FilterState<AreaData>
  | FilterState<SupervisorData>
  | FilterState<AreaControllerData>
  | FilterState<MaintenanceAdminData>
  | FilterState<SetupAreaSupervisorData>

const initialFilterState: CurrentFilterState = {
  regularFilters: {},
  dateRanges: {
    createdDate: { start: null, end: null },
    modifiedDate: { start: null, end: null },
  },
  timeRange: { start: null, end: null },
  sort: {
    field: 'id',
    direction: 'asc',
  },
}

export const AreaManagement: React.FC = () => {
  const {
    selectedTab,
    setSelectedTab,
    provinces,
    areas,
    supervisors,
    areaControllers,
    mutateProvinces,
    mutateAreas,
    mutateSupervisors,
    mutateAreaControllers,
    optimizedSearchAreaControllers,
    isAreaControllerLoading,
    optimizedDateFilterAreaControllers,
    optimizedFilterReset,
    areaControllerkeyword,
    areaControllerPage,
    areaControllerRowsPerPage,
    setAreaControllerPage,
    setAreaControllerRowsPerPage,
    areaControllerElem,
    optimizedSearchProvinces,
    isProvincesLoading,
    optimizedDateFilterProvinces,
    optimizedFilterProvinceReset,
    provinceskeyword,
    provincesPage,
    provincesRowsPerPage,
    setProvincesPage,
    setProvincesRowsPerPage,
    provincesElem,
    optimizedSearchAreas,
    isAreasLoading,
    optimizedDateFilterAreas,
    optimizedFilterAreasReset,
    areaskeyword,
    areasPage,
    areasRowsPerPage,
    setAreasPage,
    setAreasRowsPerPage,
    areasElem,
    optimizedSearchSupervisor,
    isSupervisorLoading,
    optimizedDateFilterSupervisor,
    optimizedFilterSupervisorReset,
    supervisorkeyword,
    supervisorPage,
    supervisorRowsPerPage,
    setSupervisorPage,
    setSupervisorRowsPerPage,
    supervisorElem,
    provincesList,
    areaControllerList,
    dispatch,
    optimizedSortAreas,
    optimizedSortAreaController,
    optimizedSortSupervisor,
    optimizedSortProvince,
    provincesSort,
    areasSort,
    supervisorSort,
    areaControllerSort,
    actions: { addArea },
    maintenanceAdminPage,
    maintenanceAdminRowsPerPage,
    maintenanceAdminSort,
    maintenanceAdminKeyword,
    maStartCreatedDate,
    maEndCreatedDate,
    maStartModifiedDate,
    maEndModifiedDate,
    maintenanceAdminElem,
    isMaintenanceAdminLoading,
    setupAreaSupervisorPage,
    setupAreaSupervisorRowsPerPage,
    setupAreaSupervisorSort,
    setupAreaSupervisorKeyword,
    sasStartCreatedDate,
    sasEndCreatedDate,
    sasStartModifiedDate,
    sasEndModifiedDate,
    setupAreaSupervisorElem,
    isSetupAreaSupervisorLoading,
    setMaintenanceAdminPage,
    setMaintenanceAdminRowsPerPage,
    setSetupAreaSupervisorPage,
    setSetupAreaSupervisorRowsPerPage,
    branchesList,
    setupAreaSupervisors,
    mutateSetupAreaSupervisors,
    maintenanceAdmins,
    mutateMaintenanceAdmins,
    optimizedSortMaintenanceAdmin,
    optimizedSortSAS,
    optimizedSearchMaintenanceAdmin,
    optimizedSearchSetupAreaSupervisor,
    optimizedDateFilterSetupAreaSupervisor,
    optimizedDateFilterMaintenanceAdmin,
    allAreas,
    fetchAllAreas,
    handleDeleteArea,
  } = useAreaManagementContext()

  const navigate = useNavigate()
  const role = JSON.parse(localStorage.getItem('role') || '')

  useEffect(() => {
    if (role !== 'Super Admin') {
      navigate('/maintenance-tickets')
    }
  }, [role, navigate])

  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [areaControllerFilterModalOpen, setAreaControllerFilterModalOpen] =
    useState(false)
  const [provincesFilterModalOpen, setProvincesFilterModalOpen] =
    useState(false)
  const [areasFilterModalOpen, setAreasFilterModalOpen] = useState(false)
  const [supervisorFilterModalOpen, setSupervisorFilterModalOpen] =
    useState(false)
  const [maintenanceAdminFilterModalOpen, setMaintenanceAdminFilterModalOpen] =
    useState(false)
  const [
    setupAreaSupervisorFilterModalOpen,
    setSetupAreaSupervisorFilterModalOpen,
  ] = useState(false)

  const [error, setError] = useState('')

  const { handleAlertMessage, ...alertRest } = useAlert()

  const { showModal, handleAddData, handleCloseModal } = useForm()

  const [filterState, setFilterState] =
    useState<CurrentFilterState>(initialFilterState)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('success')
  const [isRefreshing, setIsRefreshing] = useState(false)

  const refreshTable = useCallback(async () => {
    setIsRefreshing(true)
    try {
      switch (selectedTab) {
        // case 'Province':
        //   await mutateProvinces()
        //   break
        case 'Area':
          await mutateAreas()
          break
        case 'Supervisor':
          await mutateSupervisors()
          break
        case 'Area Controller':
          await mutateAreaControllers()
          break
        case 'Maintenance Admin':
          await mutateMaintenanceAdmins()
          break
        case 'Setup Area Supervisor':
          await mutateSetupAreaSupervisors()
          break
      }
      setSnackbarMessage('Data refreshed successfully')
      setSnackbarSeverity('success')
    } catch (error) {
      console.error('Error refreshing table:', error)
      setSnackbarMessage('Failed to refresh table')
      setSnackbarSeverity('error')
    } finally {
      setIsRefreshing(false)
      setOpenSnackbar(true)
    }
  }, [
    selectedTab,
    mutateProvinces,
    mutateAreas,
    mutateSupervisors,
    mutateAreaControllers,
    mutateSetupAreaSupervisors,
    mutateMaintenanceAdmins,
  ])

  const handleFilterOpenAreaController = useCallback(
    () => setAreaControllerFilterModalOpen(true),
    [],
  )
  const handleFilterCloseAreaController = useCallback(
    () => setAreaControllerFilterModalOpen(false),
    [],
  )

  const handleFilterOpenProvinces = useCallback(
    () => setProvincesFilterModalOpen(true),
    [],
  )
  const handleFilterCloseProvinces = useCallback(
    () => setProvincesFilterModalOpen(false),
    [],
  )

  const handleFilterOpenAreas = useCallback(
    () => setAreasFilterModalOpen(true),
    [],
  )
  const handleFilterCloseAreas = useCallback(
    () => setAreasFilterModalOpen(false),
    [],
  )

  const handleFilterOpenSupervisor = useCallback(
    () => setSupervisorFilterModalOpen(true),
    [],
  )
  const handleFilterCloseSupervisor = useCallback(
    () => setSupervisorFilterModalOpen(false),
    [],
  )

  const handleFilterOpenMaintenanceAdmin = useCallback(
    () => setMaintenanceAdminFilterModalOpen(true),
    [],
  )
  const handleFilterCloseMaintenanceAdmin = useCallback(
    () => setMaintenanceAdminFilterModalOpen(false),
    [],
  )

  const handleFilterOpenSetupAreaSupervisor = useCallback(
    () => setSetupAreaSupervisorFilterModalOpen(true),
    [],
  )
  const handleFilterCloseSetupAreaSupervisor = useCallback(
    () => setSetupAreaSupervisorFilterModalOpen(false),
    [],
  )

  const handleSearchAreaControllerKeyword = (keyword: string) => {
    optimizedSearchAreaControllers(keyword)
  }

  const handleSearchProvincesKeyword = (keyword: string) => {
    optimizedSearchProvinces(keyword)
  }

  const handleSearchAreasKeyword = (keyword: string) => {
    optimizedSearchAreas(keyword)
  }

  const handleSearchSupervisorKeyword = (keyword: string) => {
    optimizedSearchSupervisor(keyword)
  }

  const handleSearchMaintenanceAdminKeyword = (keyword: string) => {
    optimizedSearchMaintenanceAdmin(keyword)
  }

  const handleSearchSetupAreaSupervisorKeyword = (keyword: string) => {
    optimizedSearchSetupAreaSupervisor(keyword)
  }

  const handleResetAreaControllerFilter = useCallback(() => {
    setFilterState(initialFilterState)

    optimizedFilterReset()
    handleFilterCloseAreaController()
  }, [])

  const handleResetProvinceFilter = useCallback(() => {
    setFilterState(initialFilterState)

    optimizedFilterProvinceReset()
    handleFilterCloseProvinces()
  }, [])

  const handleResetAreasFilter = useCallback(() => {
    setFilterState(initialFilterState)

    optimizedFilterAreasReset()
    handleFilterCloseAreas()
  }, [])

  const handleResetSupervisorFilter = useCallback(() => {
    setFilterState(initialFilterState)

    optimizedFilterSupervisorReset()
    handleFilterCloseSupervisor()
  }, [])

  const handleResetMaintenanceAdminFilter = useCallback(() => {
    // Reset filter state for Maintenance Admin
    console.log('callled')
    setFilterState(initialFilterState)
    optimizedFilterReset()
    handleFilterCloseMaintenanceAdmin()
  }, [])

  const handleResetSuperAreaSupervisorFilter = useCallback(() => {
    // Reset filter state for Super Area Supervisor
    setFilterState(initialFilterState)
    optimizedFilterReset()
    handleFilterCloseSetupAreaSupervisor()
  }, [])

  const handleFilterSortedAreas = (sort: string, direction: 'asc' | 'desc') => {
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: sort as string,
        direction: direction,
      },
    }))
    optimizedSortAreas(`${sort},${direction}`)
  }

  const handleFilterSortedAreaController = (
    sort: string,
    direction: 'asc' | 'desc',
  ) => {
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: sort as string,
        direction: direction,
      },
    }))
    optimizedSortAreaController(`${sort},${direction}`)
  }

  const handleFilterSortedMaintenanceAdmin = (
    sort: string,
    direction: 'asc' | 'desc',
  ) => {
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: sort as string,
        direction: direction,
      },
    }))
    optimizedSortMaintenanceAdmin(`${sort},${direction}`)
  }

  const handleFilterSortedSuperAreaSupervisor = (
    sort: string,
    direction: 'asc' | 'desc',
  ) => {
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: sort as string,
        direction: direction,
      },
    }))
    optimizedSortSAS(`${sort},${direction}`)
  }

  const handleFilterSortedProvince = (
    sort: string,
    direction: 'asc' | 'desc',
  ) => {
    // alert("test")
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: sort as string,
        direction: direction,
      },
    }))
    optimizedSortProvince(`${sort},${directioOn}`)
  }

  const handleFilterSortedSupervisor = (
    sort: string,
    direction: 'asc' | 'desc',
  ) => {
    setFilterState((prev) => ({
      ...prev,
      sort: {
        field: sort as string,
        direction: direction,
      },
    }))
    optimizedSortSupervisor(`${sort},${direction}`)
  }
  const handleAddArea = async (data: number[], name: string, code: string) => {
    const newData = {
      name,
      code,
      branchIDs: data,
    }

    try {
      await addArea(newData, setError)(dispatch)
      await mutateAreas()
      await fetchAllAreas()
      setSnackbarMessage('Area created successfully.')
      setSnackbarSeverity('success')
      setOpenSnackbar(true)
    } catch (error) {
      console.error('Failed to add area or mutate:', error)
      setSnackbarMessage('Failed to create area.')
      setSnackbarSeverity('error')
      setOpenSnackbar(true)
    }
  }

  const handleSyncArea = async () => {
    try {
      const response = await syncArea() // Directly call the syncArea function from context
      if (response.data.success) {
        setSnackbarMessage('Areas synced successfully!')
        setSnackbarSeverity('success')
      } else {
        setSnackbarMessage('Failed to sync areas.')
        setSnackbarSeverity('error')
      }
    } catch (error) {
      setSnackbarMessage('An error occurred during sync.')
      setSnackbarSeverity('error')
    }
    setOpenSnackbar(true)
  }

  const handleSyncProvince = async () => {
    try {
      const response = await syncProvinces() // Directly call the syncArea function from context
      if (response.data.success) {
        setSnackbarMessage('Areas synced successfully!')
        setSnackbarSeverity('success')
      } else {
        setSnackbarMessage('Failed to sync areas.')
        setSnackbarSeverity('error')
      }
    } catch (error) {
      setSnackbarMessage('An error occurred during sync.')
      setSnackbarSeverity('error')
    }
    setOpenSnackbar(true)
  }

  const handleApplyFilter = useCallback(
    (newFilterState: CurrentFilterState) => {
      setFilterState(newFilterState)
    },
    [],
  )

  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setFilterState((prevState) => ({
      ...prevState,
      sort: { field, direction },
    }))
  }, [])

  const handleSelect = useCallback(
    (tabType: ManagementTabType) => {
      setSelectedTab(tabType)
      setFilterState(initialFilterState)
      setFilterModalOpen(false)
    },
    [setSelectedTab],
  )

  // Handle data validation based on selected tab
  const validatedData = useMemo(() => {
    switch (selectedTab) {
      // case 'Province':
      //   return provinces || []
      case 'Area':
        return areas || []
      case 'Supervisor':
        return supervisors || []
      case 'Area Controller':
        return areaControllers || []
      case 'Maintenance Admin':
        return maintenanceAdmins || []
      case 'Setup Area Supervisor':
        return setupAreaSupervisors || []
      default:
        return []
    }
  }, [
    selectedTab,
    provinces,
    areas,
    supervisors,
    areaControllers,
    maintenanceAdmins,
    setupAreaSupervisors,
  ])

  const handleUpdateData = useCallback((data: any) => {
    // Implement your update logic here
    console.log('Updating data:', data)
  }, [])

  const actionButton = useMemo(() => {
    switch (selectedTab) {
      // case 'Province':
      //   return (
      //     <ActionButton onClick={handleSyncProvince}>
      //       <img src={SyncIcon} alt="SyncProvinces" width={16} height={16} />
      //       Sync Provinces
      //     </ActionButton>
      //   )
      case 'Area':
        return (
          <ActionButton onClick={handleAddData}>
            <img src={AssignIcon} alt="CreateArea" width={16} height={16} />
            Create Area
          </ActionButton>
        )
      default:
        return null
    }
  }, [selectedTab])

  useEffect(() => {
    fetchAllAreas()
  }, [])

  const renderTable = useMemo(() => {
    const baseAreaControllerProps = {
      isLoading: isAreaControllerLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: areaControllerPage,
      setPage: setAreaControllerPage,
      rowsPerPage: areaControllerRowsPerPage,
      setRowsPerPage: setAreaControllerRowsPerPage,
      totalElem: areaControllerElem,
      handleUpdate: handleUpdateData,
      refreshTable,
      handleSort: handleFilterSortedAreaController,
    }

    const baseProvinceProps = {
      isLoading: isProvincesLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: provincesPage,
      setPage: setProvincesPage,
      rowsPerPage: provincesRowsPerPage,
      setRowsPerPage: setProvincesRowsPerPage,
      totalElem: provincesElem,
      handleUpdate: handleUpdateData,
      refreshTable,
      handleSort: handleFilterSortedProvince,
    }

    const baseAreaProps = {
      isLoading: isAreasLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: areasPage,
      setPage: setAreasPage,
      rowsPerPage: areasRowsPerPage,
      setRowsPerPage: setAreasRowsPerPage,
      totalElem: areasElem,
      handleUpdate: handleUpdateData,
      refreshTable,
      handleSort: handleFilterSortedAreas,
      handleDeleteArea: handleDeleteArea,
    }

    const baseSupervisorProps = {
      isLoading: isSupervisorLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: supervisorPage,
      setPage: setSupervisorPage,
      rowsPerPage: supervisorRowsPerPage,
      setRowsPerPage: setSupervisorRowsPerPage,
      totalElem: supervisorElem,
      handleUpdate: handleUpdateData,
      refreshTable,
      handleSort: handleFilterSortedSupervisor,
    }

    const maintenanceAdminProps = {
      isLoading: isMaintenanceAdminLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: maintenanceAdminPage,
      setPage: setMaintenanceAdminPage,
      rowsPerPage: maintenanceAdminRowsPerPage,
      setRowsPerPage: setMaintenanceAdminRowsPerPage,
      totalElem: maintenanceAdminElem,
      handleUpdate: handleUpdateData,
      refreshTable,
      handleSort: handleFilterSortedMaintenanceAdmin,
    }

    const superAdvisorProps = {
      isLoading: isSetupAreaSupervisorLoading,
      onSort: handleSort,
      error: null,
      filterState,
      page: setupAreaSupervisorPage,
      setPage: setSetupAreaSupervisorPage,
      rowsPerPage: setupAreaSupervisorRowsPerPage,
      setRowsPerPage: setSetupAreaSupervisorRowsPerPage,
      totalElem: setupAreaSupervisorElem,
      handleUpdate: handleUpdateData,
      refreshTable,
      handleSort: handleFilterSortedSuperAreaSupervisor,
    }

    switch (selectedTab) {
      // case 'Province':
      //   return (
      //     <AreaManagementProvinceTable
      //       {...baseProvinceProps}
      //       data={validatedData as ProvinceData[]}
      //     />
      //   )
      case 'Area':
        return (
          <AreaManagementAreaTable
            {...baseAreaProps}
            data={validatedData as AreaData[]}
            branches={branchesList || []}
            onAreaAssigned={async () => {
              await Promise.all([mutateAreas()])
            }}
          />
        )
      case 'Area Controller':
        return (
          <AreaManagementAreaControllerTable
            {...baseAreaControllerProps}
            data={validatedData as AreaControllerData[]}
            areas={allAreas || []}
            onAreaAssigned={async () => {
              await Promise.all([mutateAreaControllers(), mutateAreas()])
            }}
          />
        )
      case 'Maintenance Admin':
        return (
          <AreaManagementMaintenanceAdminTable
            {...maintenanceAdminProps}
            data={validatedData as AreaControllerData[]}
            areas={allAreas || []}
            onAreaAssigned={async () => {
              await Promise.all([mutateAreaControllers(), mutateAreas()])
            }}
          />
        )
      case 'Supervisor':
        return (
          <AreaManagementSupervisorTable
            {...baseSupervisorProps}
            data={validatedData as SupervisorData[]}
            areaControllers={areaControllerList as AreaControllerData[]}
            onSupervisorAssigned={async () => {
              await Promise.all([mutateAreaControllers(), mutateAreas()])
            }}
          />
        )
      case 'Setup Area Supervisor':
        return (
          <AreaManagementSetupAreaSupervisorTable
            {...superAdvisorProps}
            data={validatedData as AreaControllerData[]}
            areas={allAreas || []}
            onAreaAssigned={async () => {
              await Promise.all([mutateAreaControllers(), mutateAreas()])
            }}
          />
        )
      default:
        return null
    }
  }, [
    selectedTab,
    validatedData,
    isRefreshing,
    filterState,
    handleSort,
    handleUpdateData,
    refreshTable,
    provinces,
    areas,
    areaControllers,
    isAreaControllerLoading,
    areaControllerPage,
    areaControllerRowsPerPage,
    setAreaControllerPage,
    setAreaControllerRowsPerPage,
    isProvincesLoading,
    provincesPage,
    provincesRowsPerPage,
    setProvincesPage,
    setProvincesRowsPerPage,
    isAreasLoading,
    areasPage,
    areasRowsPerPage,
    setAreasPage,
    setAreasRowsPerPage,
    isSupervisorLoading,
    supervisorPage,
    supervisorRowsPerPage,
    setSupervisorPage,
    setSupervisorRowsPerPage,
    isMaintenanceAdminLoading,
    maintenanceAdminPage,
    maintenanceAdminRowsPerPage,
    setMaintenanceAdminPage,
    setMaintenanceAdminRowsPerPage,
    isSetupAreaSupervisorLoading,
    setupAreaSupervisorPage,
    setupAreaSupervisorRowsPerPage,
    setSetupAreaSupervisorPage,
    setSetupAreaSupervisorRowsPerPage,
    areaskeyword,
    provincesSort,
    areasSort,
    supervisorSort,
    areaControllerSort,
  ])

  console.log('provinces ', validatedData)

  return (
    <Container>
      <Grid2 container justifyContent="space-between" alignItems="center">
        {selectedTab === 'Area Controller' && (
          <Searchbar
            handleSearchKeyword={handleSearchAreaControllerKeyword}
            keyword={areaControllerkeyword}
          />
        )}
        {/* {selectedTab === 'Province' && (
          <Searchbar
            handleSearchKeyword={handleSearchProvincesKeyword}
            keyword={provinceskeyword}
          />
        )} */}
        {selectedTab === 'Area' && (
          <Searchbar
            handleSearchKeyword={handleSearchAreasKeyword}
            keyword={areaskeyword}
          />
        )}
        {selectedTab === 'Supervisor' && (
          <Searchbar
            handleSearchKeyword={handleSearchSupervisorKeyword}
            keyword={supervisorkeyword}
          />
        )}
        {selectedTab === 'Setup Area Supervisor' && (
          <Searchbar
            handleSearchKeyword={handleSearchSetupAreaSupervisorKeyword}
            keyword={setupAreaSupervisorKeyword}
          />
        )}
        {selectedTab === 'Maintenance Admin' && (
          <Searchbar
            handleSearchKeyword={handleSearchMaintenanceAdminKeyword}
            keyword={maintenanceAdminKeyword}
          />
        )}

        <Grid2 display="flex" gap={2}>
          {actionButton}
          {selectedTab === 'Area Controller' && (
            <FilterButton
              onClick={handleFilterOpenAreaController}
              icon={FilterIcon}
              width={16}
              height={16}
            />
          )}
          {/* {selectedTab === 'Province' && (
            <FilterButton
              onClick={handleFilterOpenProvinces}
              icon={FilterIcon}
              width={16}
              height={16}
            />
          )} */}
          {selectedTab === 'Area' && (
            <FilterButton
              onClick={handleFilterOpenAreas}
              icon={FilterIcon}
              width={16}
              height={16}
            />
          )}
          {selectedTab === 'Supervisor' && (
            <FilterButton
              onClick={handleFilterOpenSupervisor}
              icon={FilterIcon}
              width={16}
              height={16}
            />
          )}
          {selectedTab === 'Maintenance Admin' && (
            <FilterButton
              onClick={handleFilterOpenMaintenanceAdmin}
              icon={FilterIcon}
              width={16}
              height={16}
            />
          )}
          {selectedTab === 'Setup Area Supervisor' && (
            <FilterButton
              onClick={handleFilterOpenSetupAreaSupervisor}
              icon={FilterIcon}
              width={16}
              height={16}
            />
          )}
        </Grid2>
      </Grid2>

      <Grid2 container flexDirection="column">
        <Grid2
          display="flex"
          gap={1}
          mt={1}
          sx={{ transform: 'translateY(7.5px)', zIndex: 0 }}
        >
          {/* <ButtonTab
            buttontype="Province"
            selected={selectedTab}
            onClick={() => handleSelect('Province')}
          >
            Province
          </ButtonTab> */}
          <ButtonTab
            buttontype="Area"
            selected={selectedTab}
            onClick={() => handleSelect('Area')}
          >
            Area
          </ButtonTab>
          <ButtonTab
            buttontype="Area Controller"
            selected={selectedTab}
            onClick={() => handleSelect('Area Controller')}
          >
            Area Controller
          </ButtonTab>

          <ButtonTab
            buttontype="Maintenance Admin"
            selected={selectedTab}
            onClick={() => handleSelect('Maintenance Admin')}
          >
            Maintenance Admin
          </ButtonTab>

          <ButtonTab
            buttontype="Setup Area Supervisor"
            selected={selectedTab}
            onClick={() => handleSelect('Setup Area Supervisor')}
          >
            Setup Area Supervisor
          </ButtonTab>
          {/* <ButtonTab
            buttontype="Supervisor"
            selected={selectedTab}
            onClick={() => handleSelect('Supervisor')}
          >
            Supervisor
          </ButtonTab> */}
        </Grid2>

        {renderTable}
      </Grid2>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar for filter errors using the useAlert util */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={alertRest.showAlert}
        onClose={alertRest.handleCloseAlert}
        autoHideDuration={3000}
      >
        <Alert
          onClose={alertRest.handleCloseAlert}
          elevation={6}
          variant="filled"
          severity={alertRest.alertSeverity}
        >
          {alertRest.alertMessage}
        </Alert>
      </Snackbar>

      {areaControllerFilterModalOpen && selectedTab === 'Area Controller' && (
        <FilterAreaControllerModal<AreaControllerData>
          open={areaControllerFilterModalOpen}
          onClose={handleFilterCloseAreaController}
          config={
            areaControllerFilterConfig() as FilterConfig<AreaControllerData>
          }
          onApplyFilter={handleApplyFilter}
          initialFilters={filterState}
          optimizedDateFilterAreaControllers={
            optimizedDateFilterAreaControllers
          }
          tab={''}
          handleAlertMessage={handleAlertMessage}
          handleReset={handleResetAreaControllerFilter}
        />
      )}
      {/* 
      {provincesFilterModalOpen && selectedTab === 'Province' && (
        <FilterProvincesModal<AreaControllerData>
          open={provincesFilterModalOpen}
          onClose={handleFilterCloseProvinces}
          config={
            createProvinceManagementFilterConfig() as FilterConfig<ProvinceData>
          }
          onApplyFilter={handleApplyFilter}
          initialFilters={filterState}
          optimizedDateFilterProvinces={optimizedDateFilterProvinces}
          tab={''}
          handleAlertMessage={handleAlertMessage}
          handleReset={handleResetProvinceFilter}
        />
      )} */}

      {areasFilterModalOpen && selectedTab === 'Area' && (
        <FilterAreasModal<AreaData>
          open={areasFilterModalOpen}
          onClose={handleFilterCloseAreas}
          config={areaManagementFilterConfig() as FilterConfig<AreaData>}
          onApplyFilter={handleApplyFilter}
          initialFilters={filterState}
          optimizedDateFilterAreas={optimizedDateFilterAreas}
          tab={''}
          handleAlertMessage={handleAlertMessage}
          handleReset={handleResetAreasFilter}
        />
      )}

      {maintenanceAdminFilterModalOpen &&
        selectedTab === 'Maintenance Admin' && (
          <FilterMaintenanceAdminModal<AreaControllerData>
            open={maintenanceAdminFilterModalOpen}
            onClose={handleFilterCloseMaintenanceAdmin}
            config={
              areaControllerFilterConfig() as FilterConfig<AreaControllerData>
            }
            onApplyFilter={handleApplyFilter}
            initialFilters={filterState}
            optimizedDateFilterMaintenanceAdmins={
              optimizedDateFilterMaintenanceAdmin
            }
            tab={''}
            handleAlertMessage={handleAlertMessage}
            handleReset={handleResetMaintenanceAdminFilter}
          />
        )}

      {setupAreaSupervisorFilterModalOpen &&
        selectedTab === 'Setup Area Supervisor' && (
          <FilterSetupAreaSupervisorModal<AreaControllerData>
            open={setupAreaSupervisorFilterModalOpen}
            onClose={handleFilterCloseSetupAreaSupervisor}
            config={
              areaControllerFilterConfig() as FilterConfig<AreaControllerData>
            }
            onApplyFilter={handleApplyFilter}
            initialFilters={filterState}
            optimizedDateFiltersSAS={optimizedDateFilterSetupAreaSupervisor}
            tab={''}
            handleAlertMessage={handleAlertMessage}
            handleReset={handleResetSuperAreaSupervisorFilter}
          />
        )}

      {areaControllerFilterModalOpen && selectedTab === 'Area Controller' && (
        <FilterAreaControllerModal<AreaControllerData>
          open={areaControllerFilterModalOpen}
          onClose={handleFilterCloseAreaController}
          config={
            areaControllerFilterConfig() as FilterConfig<AreaControllerData>
          }
          onApplyFilter={handleApplyFilter}
          initialFilters={filterState}
          optimizedDateFilterAreaControllers={
            optimizedDateFilterAreaControllers
          }
          tab={''}
          handleAlertMessage={handleAlertMessage}
          handleReset={handleResetAreaControllerFilter}
        />
      )}

      {supervisorFilterModalOpen && selectedTab === 'Setup Area Supervisor' && (
        <FilterSupervisorsModal<SupervisorData>
          open={supervisorFilterModalOpen}
          onClose={handleFilterCloseSupervisor}
          config={supervisorFilterConfig() as FilterConfig<SupervisorData>}
          onApplyFilter={handleApplyFilter}
          initialFilters={filterState}
          optimizedDateFilterSupervisor={optimizedDateFilterSupervisor}
          tab={''}
          handleAlertMessage={handleAlertMessage}
          handleReset={handleResetSupervisorFilter}
        />
      )}

      {showModal && (
        <AreaManagementForm
          open={showModal}
          onClose={handleCloseModal}
          onAdd={handleAddArea}
          branchList={branchesList || []}
          error={error}
          areas={allAreas || []}
        />
      )}
    </Container>
  )
}
